import React from 'react';
import {
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Edit,
} from 'lucide-react';
import moment from 'moment';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import { makeStyles } from '@mui/styles';

interface PostCardProps {
  post: any;
  onDelete: (id: number) => void;
  onOpenPanel: (post: PostCardProps['post']) => void;
}

const useStyles = makeStyles({
  overflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '95%',
  },
  themeButtons: {
    width: '65px',
    height: '25px',
    display: 'flex',
    padding: '2px 0',
    justifyContent: 'center',
    borderRadius: '12px',
    alignItems: 'center',
    fontSize: '10px',
    '&.published': {
      width: '65px !important',
      border: '1px solid #C0F3C6',
      backgroundColor: '#d1fae5',
      color: '#238E30',
    },
    '&.draft': {
      border: '1px solid #FCE68A',
      backgroundColor: '#fef3c7',
      color: '#f59e0b',
    },
    '&.scheduled': {
      border: '1px solid #BFDBFE',
      backgroundColor: '#fed7aa',
      color: '#ea580c',
    },
  },
});

export default function PostCard({
  post,
  onDelete,
  onOpenPanel,
}: PostCardProps) {
  const classes = useStyles();
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram size={12} className="text-[#6B7280]" />;
      case 'facebook':
        return <Facebook size={12} className="text-[#6B7280]" />;
      case 'YouTube':
        return <Youtube size={12} className="text-[#6B7280]" />;
      case 'linkedin':
        return <Linkedin size={12} className="text-[#6B7280]" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden p-4 gap-3">
      <div className="flex-shrink-0">
        <span className={`items-center ${classes.themeButtons} ${post.publishedStatus === 'Published' ? 'published' : post.publishedStatus === 'Draft' ? 'draft' : 'scheduled'}`}>
          {post.publishedStatus}
        </span>
      </div>

      {/* Media Preview */}
      <div className="flex-shrink-0">
        <VideoPlayer videoUrl={post?.url || ''} thumbnail={post?.thumbnail} />
      </div>

      {/* Caption Preview - 3-4 lines max */}
      <div className="flex-shrink-0">
        <p className={`text-sm text-[#374151] line-clamp-4 leading-relaxed ${classes.overflow}`}>
          {post.title}
        </p>
      </div>

      {/* Metadata Row */}
      <div className="flex-shrink-0 flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
        <span className="text-xs text-[#9CA3AF]">{post?.publishedDate ? moment(post?.publishedDate).format('YYYY-MM-DD') : ''}</span>

        {/* Platform Icons */}
        {post.platforms?.length > 0 && (
          <div className="flex items-center gap-1">
            {post.platforms?.map((platform: string, index: number) => (
              <span key={index}>{getPlatformIcon(platform)}</span>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <button
          onClick={() => onOpenPanel(post)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#F67570] hover:bg-[#F45C56] rounded transition-colors"
          title="Continue Editing"
        >
          <Edit size={13} />
          <span>Continue</span>
        </button>
      </div>
    </div>
  );
}
